// News.js (ES Module)
import { gsap } from 'gsap'
import { ScrollToPlugin } from 'gsap/ScrollToPlugin'
gsap.registerPlugin(ScrollToPlugin)

export default class News {
  /**
   * @param {Object} options
   * @param {string} [options.listSelector='.p-news__list']
   * @param {string} [options.filterListSelector='.js-filter-list']
   * @param {string} [options.filterRootSelector='.js-filter']
   * @param {number} [options.scrollOffset=120]
   * @param {string} [options.loadingClass='is-loading']
   */
  constructor(options = {}) {
    this.opts = {
      listSelector: '.p-news__list',
      filterListSelector: '.js-filter-list',
      filterRootSelector: '.js-filter',
      moreBtnSelector: '.js-news-list-more',
      moreFooterSelector: '.js-news-footer',
      perPage: 24,
      revealDuration: 0.6,
      revealStagger: 0.05,
      scrollOffset: 120,
      loadingClass: 'is-loading',
      ...options,
    }
    this.list = null
    this.filterList = null
    this.filterRoot = null
    this.moreBtn = null
    this.moreFooter = null
    this.isBusy = false
    this.config = null // { ajaxUrl, nonce, taxonomy }
    this.items = []
    this.visibleCount = 0

    this.onClickFilter = this.onClickFilter.bind(this)
    this.onPopState = this.onPopState.bind(this)
    this.onClickMore = this.onClickMore.bind(this)

    this.init()
  }

  init() {
    this.list = document.querySelector(this.opts.listSelector)
    this.filterList = document.querySelector(this.opts.filterListSelector)
    this.filterRoot = document.querySelector(this.opts.filterRootSelector)
    this.moreBtn = document.querySelector(this.opts.moreBtnSelector)
    this.moreFooter = document.querySelector(this.opts.moreFooterSelector)

    if (this.list && this.moreBtn) {
      this.initLoadMore()
    }

    if (!this.list || !this.filterList) return

    // 1) data-属性を最優先、2) window.NEWS_AJAX をフォールバック
    this.config = this.getConfig()
    if (!this.config) {
      console.warn('[News] config not found (no data-* and no window.NEWS_AJAX)')
      return
    }

    gsap.set(this.list, { autoAlpha: 1 })
    this.initAjaxFilter()
  }

  /** 一覧の「もっと見る」（24件ずつ表示） */
  initLoadMore() {
    this.items = [...this.list.querySelectorAll('.c-news-list__item')]
    this.visibleCount = Math.min(this.opts.perPage, this.items.length)

    this.items.forEach((item, index) => {
      item.hidden = index >= this.visibleCount
    })

    this.updateMoreButton()
    this.moreBtn.addEventListener('click', this.onClickMore)
  }

  resetLoadMore() {
    this.moreBtn?.removeEventListener('click', this.onClickMore)
    if (this.list && this.moreBtn) {
      this.initLoadMore()
    }
  }

  onClickMore() {
    const nextCount = Math.min(this.visibleCount + this.opts.perPage, this.items.length)
    const newItems = this.items.slice(this.visibleCount, nextCount)

    this.revealItems(newItems)
    this.visibleCount = nextCount
    this.updateMoreButton()
  }

  /** 追加表示（u-fade-in-list と同じ 0.6s / 0.05s スタッガー） */
  revealItems(items) {
    if (!items.length) return

    items.forEach((item) => {
      item.hidden = false
    })

    gsap.fromTo(
      items,
      { opacity: 0 },
      {
        opacity: 1,
        duration: this.opts.revealDuration,
        ease: 'power2.out',
        stagger: this.opts.revealStagger,
        overwrite: 'auto',
        onStart: () => {
          items.forEach((item) => {
            item.style.transition = 'none'
          })
        },
        onComplete: () => {
          items.forEach((item) => {
            item.style.transition = ''
          })
        },
      },
    )
  }

  updateMoreButton() {
    const total = this.items.length
    const hasMore = total > this.opts.perPage && this.visibleCount < total
    const target = this.moreFooter || this.moreBtn

    if (!target) return

    target.hidden = !hasMore
  }

  /** data属性 or window.NEWS_AJAX から設定を取得 */
  getConfig() {
    const el = this.list
    const ajaxUrl = el?.dataset?.ajaxUrl
    const nonce = el?.dataset?.nonce
    const taxonomy = el?.dataset?.taxonomy || 'news_cat'

    if (ajaxUrl && nonce) {
      return { ajaxUrl, nonce, taxonomy }
    }
    // フォールバック（従来の wp_localize_script）
    if (window.NEWS_AJAX?.ajaxUrl && window.NEWS_AJAX?.nonce) {
      return {
        ajaxUrl: window.NEWS_AJAX.ajaxUrl,
        nonce: window.NEWS_AJAX.nonce,
        taxonomy: window.NEWS_AJAX.taxonomy || 'news_cat',
      }
    }
    return null
  }

  initAjaxFilter() {
    // Barba対策：capture で先に拾う
    this.filterList.addEventListener('click', this.onClickFilter, { capture: true })
    window.addEventListener('popstate', this.onPopState)
  }

  async onClickFilter(e) {
    const a = e.target.closest('.c-filter__list-link')
    if (!a) return
    const term = a.dataset.term
    if (!term) return

    // Barba へ届く前に止める
    e.preventDefault()
    e.stopPropagation()
    if (typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation()

    if (this.isBusy) return
    this.isBusy = true

    this.setCurrent(a)
    this.scrollToFilter()

    try {
      await this.fetchPosts({ term, urlForHistory: a.getAttribute('href') })
    } finally {
      this.isBusy = false
    }
  }

  async onPopState() {
    if (this.isBusy) return
    this.isBusy = true

    const term = this.getTermFromLocation() || 'all'
    const link = this.filterList.querySelector(`.c-filter__list-link[data-term="${CSS.escape(term)}"]`) || this.filterList.querySelector(`.c-filter__list-link[data-term="all"]`)

    if (link) this.setCurrent(link)
    this.scrollToFilter()

    try {
      await this.fetchPosts({ term, urlForHistory: null })
    } finally {
      this.isBusy = false
    }
  }

  setCurrent(el) {
    this.filterList.querySelectorAll('.c-filter__list-item').forEach((li) => li.classList.remove('is-current'))
    const li = el.closest('.c-filter__list-item')
    if (li) li.classList.add('is-current')
  }

  loadingOn() {
    this.list.classList.add(this.opts.loadingClass)
  }
  loadingOff() {
    this.list.classList.remove(this.opts.loadingClass)
  }

  /** フィルター位置 - offset までスクロール */
  scrollToFilter() {
    if (!this.filterRoot) return
    const rect = this.filterRoot.getBoundingClientRect()
    const y = window.scrollY + rect.top - this.opts.scrollOffset
    gsap.to(window, {
      duration: 0.4,
      ease: 'power2.out',
      scrollTo: { y: Math.max(y, 0), autoKill: false },
    })
  }

  fadeOutList() {
    return new Promise((resolve) => {
      gsap.to(this.list, { autoAlpha: 0, duration: 0.2, onComplete: resolve })
    })
  }
  fadeInList() {
    return new Promise((resolve) => {
      gsap.to(this.list, { autoAlpha: 1, duration: 0.25, onComplete: resolve })
    })
  }

  /**
   * 投稿取得 → フェード差し替え
   */
  async fetchPosts({ term = 'all', urlForHistory = null, paged = 1 } = {}) {
    if (!this.config) return
    this.loadingOn()

    const fetchPromise = fetch(this.config.ajaxUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8' },
      credentials: 'same-origin',
      body: new URLSearchParams({
        action: 'mc_news_filter',
        nonce: this.config.nonce,
        term,
        paged: String(paged),
      }),
    }).then((r) => r.json())

    await this.fadeOutList()

    try {
      const json = await fetchPromise
      if (json?.success) {
        this.list.innerHTML = json.data.html

        if (urlForHistory) {
          history.pushState({ term }, '', urlForHistory)
        }
        this.resetLoadMore()
        this.hookAjaxPagination(term)
      } else {
        console.warn('[News] AJAX failed or malformed response:', json)
      }
    } catch (err) {
      console.error('[News] fetch error:', err)
    } finally {
      await this.fadeInList()
      this.loadingOff()
    }
  }

  /** URL からターム推測（/news_cat/{slug} の末尾） */
  getTermFromLocation() {
    const path = location.pathname.replace(/\/+$/, '')
    const m = path.match(/\/news_cat\/([^/]+)$/)
    return m && m[1] ? decodeURIComponent(m[1]) : 'all'
  }

  /** （任意）ページネーションをAJAXへ */
  hookAjaxPagination(currentTerm) {
    const pager = this.list.querySelector('.js-news-pager')
    if (!pager) return
    pager.querySelectorAll('a[href]').forEach((a) => {
      a.addEventListener('click', (e) => {
        const url = new URL(a.href, location.origin)
        const page = Number(url.searchParams.get('paged') || url.searchParams.get('page') || '1')
        if (!page) return
        e.preventDefault()
        if (this.isBusy) return
        this.isBusy = true
        this.scrollToFilter()
        this.fetchPosts({ term: currentTerm, urlForHistory: url.toString(), paged: page }).finally(() => {
          this.isBusy = false
        })
      })
    })
  }
}
